import { createRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { appLayoutRoute, loadSession, publicLayoutRoute } from '../../router/parents';
import {
  AcceptInvitePage,
  CheckEmailPage,
  ContinuePage,
  ForgotPasswordPage,
  OnboardingPage,
  ResetPasswordPage,
  SignInPage,
  SignUpPage,
} from './auth/pages';
import { VerifyEmailPage } from './auth/verify';
import { HelpPage } from '../help/HelpPage';
import { HomePage } from './HomePage';
import { AutomationsSettingsPage } from './settings/automations';
import { BillingSettingsPage } from './settings/billing';
import {
  AuditLogPage,
  BrandingSettingsPage,
  CustomFieldsSettingsPage,
  ExportSettingsPage,
  GeneralSettingsPage,
  MembersSettingsPage,
  ProfileSettingsPage,
  RolesSettingsPage,
  SettingsLayout,
} from './settings/pages';

// ---------------------------------------------------------------------------
// Public (auth) routes
// ---------------------------------------------------------------------------

const signInRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/sign-in',
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ context, search }) => {
    if (await loadSession(context.queryClient)) throw redirect({ to: search.redirect?.startsWith('/') ? search.redirect : '/' });
  },
  component: function SignInRoute() {
    const { redirect: target } = signInRoute.useSearch();
    return <SignInPage redirect={target} />;
  },
});

const signUpRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/sign-up',
  validateSearch: z.object({ invite: z.string().optional() }),
  component: function SignUpRoute() {
    const { invite } = signUpRoute.useSearch();
    return <SignUpPage invite={invite} />;
  },
});

const checkEmailRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/check-email',
  validateSearch: z.object({ email: z.string().optional(), reason: z.enum(['signup', 'verify']).optional().catch(undefined) }),
  component: function CheckEmailRoute() {
    const { email, reason } = checkEmailRoute.useSearch();
    return <CheckEmailPage email={email} reason={reason} />;
  },
});

/** Target of the email verification link (apps/api/src/auth.ts builds it). */
const verifyEmailRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/verify-email',
  validateSearch: z.object({ token: z.string().optional(), next: z.string().optional() }),
  component: function VerifyEmailRoute() {
    const { token, next } = verifyEmailRoute.useSearch();
    return <VerifyEmailPage token={token} next={next} />;
  },
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/reset-password',
  validateSearch: z.object({ token: z.string().optional() }),
  component: function ResetPasswordRoute() {
    const { token } = resetPasswordRoute.useSearch();
    return <ResetPasswordPage token={token} />;
  },
});

const inviteRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/invite/$token',
  component: function InviteRoute() {
    const { token } = inviteRoute.useParams();
    return <AcceptInvitePage token={token} />;
  },
});

/** After a sign-in with a company code: opens that company's workspace or portal. */
const continueRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/continue',
  validateSearch: z.object({ code: z.string().max(40).catch(''), redirect: z.string().optional() }),
  beforeLoad: async ({ context, location, search }) => {
    if (!(await loadSession(context.queryClient))) throw redirect({ to: '/sign-in', search: { redirect: location.href } });
    if (!search.code) throw redirect({ to: '/' });
  },
  component: function ContinueRoute() {
    const { code, redirect: target } = continueRoute.useSearch();
    return <ContinuePage code={code} redirect={target} />;
  },
});

const onboardingRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/onboarding',
  beforeLoad: async ({ context, location }) => {
    if (!(await loadSession(context.queryClient))) throw redirect({ to: '/sign-in', search: { redirect: location.href } });
  },
  component: OnboardingPage,
});

export const workspacePublicRoutes = [
  signInRoute,
  signUpRoute,
  checkEmailRoute,
  verifyEmailRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  inviteRoute,
  continueRoute,
  onboardingRoute,
] as const;

// ---------------------------------------------------------------------------
// Signed-in app routes
// ---------------------------------------------------------------------------

const homeRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/', component: HomePage });
/** The user guide and cheat sheet (every signed-in user). */
const helpRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/help', component: HelpPage });

const settingsRoute = createRoute({ getParentRoute: () => appLayoutRoute, path: '/settings', component: SettingsLayout });
const settingsIndexRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/', component: GeneralSettingsPage });
const brandingRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/branding', component: BrandingSettingsPage });
const membersRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/members', component: MembersSettingsPage });
const rolesRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/roles', component: RolesSettingsPage });
const customFieldsRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/custom-fields', component: CustomFieldsSettingsPage });
const billingRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/billing',
  validateSearch: z.object({ checkout: z.enum(['success', 'cancelled']).optional().catch(undefined) }),
  component: function BillingRoute() {
    const { checkout } = billingRoute.useSearch();
    return <BillingSettingsPage checkout={checkout} />;
  },
});
const automationsRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/automations', component: AutomationsSettingsPage });
const auditRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/audit', component: AuditLogPage });
const exportRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/export', component: ExportSettingsPage });
const profileRoute = createRoute({ getParentRoute: () => settingsRoute, path: '/profile', component: ProfileSettingsPage });

export const workspaceAppRoutes = [
  homeRoute,
  helpRoute,
  settingsRoute.addChildren([
    settingsIndexRoute,
    brandingRoute,
    membersRoute,
    rolesRoute,
    customFieldsRoute,
    automationsRoute,
    billingRoute,
    auditRoute,
    exportRoute,
    profileRoute,
  ]),
] as const;
